import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Sparkles,
  Shield,
  TrendingUp,
  Loader2,
  ChefHat,
  Heart,
  Plane,
  ShoppingCart,
  ArrowRight,
} from "lucide-react";
import request from "../utils/request";
import PhoneDemo from "../components/PhoneDemo";
import {
  trackConciergeCancelConfirmed,
  trackConciergeCheckoutReturn,
  trackConciergeManageBillingClick,
  trackConciergeSubscriptionPageView,
  trackConciergeSubscribeClick,
  trackConciergeSubscribeError,
} from "../utils/hostAnalytics";

const PHONE_SCALE = 220 / PhoneDemo.WIDTH;

const FEATURES = [
  {
    icon: Sparkles,
    title: "Delight Your Guests",
    description:
      "Private chefs, spa treatments, airport transfers, and more — all bookable from their phone before they arrive.",
  },
  {
    icon: TrendingUp,
    title: "Earn Passive Revenue",
    description:
      "Generate additional income from every service booked through your properties with zero operational overhead.",
  },
  {
    icon: Shield,
    title: "Fully Managed",
    description:
      "Service requests, payments, and scheduling are handled end to end by Bellhopt. No extra work for you.",
  },
];

const INCLUDED_SERVICES = [
  { icon: ShoppingCart, name: "Grocery Delivery", included: true },
  { icon: ChefHat, name: "Private Chef" },
  { icon: Heart, name: "Massage & Spa" },
  { icon: Plane, name: "Airport Transfer" },
];

export default function ConciergeSubscriptionScreen() {
  const { hostInfo } = useSelector((state) => state.host);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const checkoutStatusParam = searchParams.get("status");
  const pageViewSent = useRef(false);

  const fetchStatus = () => {
    if (!hostInfo?._id) return;
    setLoading(true);
    request
      .get(`/hosts/${hostInfo._id}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ conciergeEnabled: false, status: "none" }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostInfo?._id]);

  useEffect(() => {
    if (loading || !hostInfo?._id || !status || pageViewSent.current) return;
    pageViewSent.current = true;
    trackConciergeSubscriptionPageView({
      hostId: hostInfo._id,
      subscriptionStatus: status.conciergeEnabled ? "active" : "not_subscribed",
    });
  }, [loading, hostInfo?._id, status]);

  useEffect(() => {
    if (loading || !hostInfo?._id || !checkoutStatusParam) return;
    trackConciergeCheckoutReturn({
      hostId: hostInfo._id,
      statusParam: checkoutStatusParam,
    });
  }, [loading, hostInfo?._id, checkoutStatusParam]);

  const handleSubscribe = async (tier = "basic", placement = "unknown") => {
    const basicPlan = status?.plans?.basic;
    const priceShownUsd = basicPlan
      ? basicPlan.priceInCents / 100
      : 100;
    trackConciergeSubscribeClick({
      hostId: hostInfo._id,
      source: "subscription_page",
      tier,
      placement,
      priceShownUsd,
    });
    setActionLoading(true);
    try {
      const { data } = await request.post(
        `/hosts/${hostInfo._id}/concierge/subscribe`,
        { tier }
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to start checkout";
      trackConciergeSubscribeError({
        hostId: hostInfo._id,
        source: "subscription_page",
        message: msg,
      });
      alert(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    trackConciergeManageBillingClick({ hostId: hostInfo._id });
    setActionLoading(true);
    try {
      const { data } = await request.post(
        `/hosts/${hostInfo._id}/concierge/portal`
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to open billing portal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel? Your subscription will remain active until the end of the current billing period."
      )
    )
      return;
    trackConciergeCancelConfirmed({ hostId: hostInfo._id });
    setActionLoading(true);
    try {
      await request.post(`/hosts/${hostInfo._id}/concierge/cancel`);
      fetchStatus();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to cancel");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#888888]" />
      </div>
    );
  }

  const isActive = status?.conciergeEnabled;
  const plan = status?.plans?.basic;
  const priceLabel = plan
    ? `$${(plan.priceInCents / 100).toFixed(0)}`
    : "$100";
  const statusParam = searchParams.get("status");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {statusParam === "success" && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <p className="text-sm text-emerald-800 font-medium">
            Subscription activated! Your guests can now book concierge services.
          </p>
        </motion.div>
      )}

      {isActive ? (
        <ActiveView
          status={status}
          priceLabel={priceLabel}
          onManage={handleManage}
          onCancel={handleCancel}
          actionLoading={actionLoading}
        />
      ) : (
        <PromoView
          priceLabel={priceLabel}
          plan={plan}
          onSubscribe={handleSubscribe}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
}

/* ─── Promo View (not subscribed) ─── */

function PromoView({ priceLabel, plan, onSubscribe, actionLoading }) {
  return (
    <div className="space-y-16">
      {/* ── Hero ── */}
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
        <div className="flex-1 text-center lg:text-left">
          <p className="text-[9px] font-bold tracking-[0.22em] uppercase text-[#888888] mb-4">
            Now available
          </p>

          <h1
            className="text-[34px] lg:text-[46px] font-extrabold text-[#111111] mb-4"
            style={{ lineHeight: 1.05, letterSpacing: -0.5 }}
          >
            Give your guests access
            <br />
            to premium concierge services.
          </h1>

          <p className="text-[16px] text-[#5F6368] max-w-xl mb-8 leading-relaxed">
            Private chefs, spa, airport transfers, and more — all bookable from
            your guest's phone. You choose what to offer. Grocery delivery is
            already included.
          </p>

          <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mb-10">
            <button
              onClick={() => onSubscribe("basic", "hero")}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-7 py-4 bg-[#111111] text-white text-[16px] font-bold hover:bg-[#333333] transition-colors disabled:opacity-60"
              style={{ borderRadius: 12 }}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              Get Started — {priceLabel}/mo
            </button>
            <span className="text-[13px] text-[#888888]">
              Cancel anytime. No long-term commitment.
            </span>
          </div>

          {/* What's included vs what you unlock */}
          <div className="flex flex-wrap gap-3">
            {INCLUDED_SERVICES.map((svc, i) => (
              <div
                key={i}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border"
                style={{
                  background: svc.included
                    ? "rgba(16,185,129,0.06)"
                    : "#F7F7F8",
                  borderColor: svc.included
                    ? "rgba(16,185,129,0.15)"
                    : "#EAEAEA",
                }}
              >
                <svc.icon
                  size={14}
                  className={
                    svc.included ? "text-emerald-500" : "text-[#888888]"
                  }
                  strokeWidth={1.8}
                />
                <span className="text-[12px] font-semibold text-[#111111]">
                  {svc.name}
                </span>
                {svc.included && (
                  <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-wide">
                    Free
                  </span>
                )}
              </div>
            ))}
            <div className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-[#F7F7F8] border border-[#EAEAEA]">
              <span className="text-[12px] font-semibold text-[#888888]">
                +4 more
              </span>
            </div>
          </div>
        </div>

        {/* Phone demo */}
        <div className="flex-shrink-0">
          <div className="rounded-3xl bg-[#F7F7F8] border border-[#EAEAEA] p-8 lg:p-10">
            <div
              style={{
                width: 230,
                height: 470,
                borderRadius: 38,
                background: "#000",
                border: "1px solid rgba(0,0,0,0.1)",
                position: "relative",
                boxShadow:
                  "0 25px 80px rgba(0,0,0,0.12), 0 10px 30px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 70,
                  height: 20,
                  borderRadius: 10,
                  background: "#000",
                  zIndex: 20,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 5,
                  left: 5,
                  right: 5,
                  bottom: 5,
                  borderRadius: 33,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    width: PhoneDemo.WIDTH,
                    height: PhoneDemo.HEIGHT,
                    transform: `scale(${PHONE_SCALE})`,
                    transformOrigin: "top left",
                  }}
                >
                  <PhoneDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Features ── */}
      <div>
        <div className="text-center mb-10">
          <p className="text-[9px] font-bold tracking-[0.22em] uppercase text-[#888888] mb-2">
            Why concierge
          </p>
          <h2
            className="text-[28px] font-extrabold text-[#111111]"
            style={{ letterSpacing: -0.5 }}
          >
            Everything your guests need. Nothing you have to manage.
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-[#F7F7F8] rounded-2xl border border-[#EAEAEA] p-6 hover:-translate-y-1 transition-all duration-300"
              style={{ borderRadius: 16 }}
            >
              <div className="w-10 h-10 rounded-xl bg-[#111111] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
              </div>
              <h3 className="font-bold text-[#111111] text-[16px] mb-1">
                {f.title}
              </h3>
              <p className="text-[13px] text-[#5F6368] leading-relaxed">
                {f.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Pricing ── */}
      <div className="max-w-lg mx-auto">
        <div
          className="bg-white rounded-2xl border-2 border-[#111111] p-8 sm:p-10"
          style={{ borderRadius: 24 }}
        >
          <div className="flex flex-col sm:flex-row items-center sm:items-end justify-between gap-4 mb-6">
            <div>
              <p className="text-[9px] font-bold tracking-[0.22em] uppercase text-[#888888] mb-1">
                Monthly plan
              </p>
              <div className="flex items-baseline gap-1">
                <span
                  className="text-[48px] font-extrabold text-[#111111]"
                  style={{ letterSpacing: -1, lineHeight: 1 }}
                >
                  {priceLabel}
                </span>
                <span className="text-[18px] text-[#888888] font-medium">
                  /mo
                </span>
              </div>
            </div>
            <span className="text-[13px] text-[#888888]">Cancel anytime</span>
          </div>

          <div
            className="h-px w-full mb-6"
            style={{ background: "rgba(0,0,0,0.06)" }}
          />

          <ul className="space-y-3 mb-8">
            {(
              plan?.features || [
                "All concierge services for your guests",
                "Automated request & payment handling",
                "In-app guest booking experience",
                "Revenue from every service booked",
                "Pre-arrival grocery delivery included free",
              ]
            ).map((f, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-[14px] text-[#5F6368]">{f}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={() => onSubscribe("basic", "pricing")}
            disabled={actionLoading}
            className="w-full flex items-center justify-center gap-2 py-4 bg-[#111111] text-white text-[16px] font-bold hover:bg-[#333333] transition-colors disabled:opacity-60"
            style={{ borderRadius: 12 }}
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Get Started
                <ArrowRight size={16} strokeWidth={2.5} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Active View (subscribed) ─── */

function ActiveView({
  status,
  priceLabel,
  onManage,
  onCancel,
  actionLoading,
}) {
  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-extrabold text-[#111111]"
            style={{ letterSpacing: -0.5 }}
          >
            Concierge Subscription
          </h1>
          <p className="text-[14px] text-[#5F6368] mt-1">
            Manage your concierge service subscription
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5" />
            {status.cancelAtPeriodEnd ? "Canceling" : "Active"}
          </span>
          {status.tier && status.tier !== "none" && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-medium bg-[#F7F7F8] text-[#5F6368] capitalize border border-[#EAEAEA]">
              {status.tier}
            </span>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div
          className="bg-[#F7F7F8] rounded-2xl border border-[#EAEAEA] p-6"
          style={{ borderRadius: 16 }}
        >
          <h3 className="text-[9px] font-bold tracking-[0.22em] uppercase text-[#888888] mb-5">
            Plan details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-[14px] text-[#5F6368]">Plan</span>
              <span className="text-[14px] font-semibold text-[#111111]">
                {priceLabel}/month
              </span>
            </div>
            {status.currentPeriodEnd && (
              <div className="flex justify-between">
                <span className="text-[14px] text-[#5F6368]">
                  {status.cancelAtPeriodEnd ? "Active until" : "Next billing"}
                </span>
                <span className="text-[14px] font-semibold text-[#111111]">
                  {new Date(status.currentPeriodEnd).toLocaleDateString(
                    "en-US",
                    { month: "short", day: "numeric", year: "numeric" }
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={onManage}
              disabled={actionLoading}
              className="flex-1 py-3 bg-[#111111] text-white text-[14px] font-bold hover:bg-[#333333] transition-colors disabled:opacity-60"
              style={{ borderRadius: 12 }}
            >
              {actionLoading ? "Loading..." : "Manage Billing"}
            </button>
            {!status.cancelAtPeriodEnd && (
              <button
                onClick={onCancel}
                disabled={actionLoading}
                className="px-5 py-3 text-red-500 text-[14px] font-semibold border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-60"
                style={{ borderRadius: 12 }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div
          className="bg-[#F7F7F8] rounded-2xl border border-[#EAEAEA] p-6"
          style={{ borderRadius: 16 }}
        >
          <h3 className="text-[9px] font-bold tracking-[0.22em] uppercase text-[#888888] mb-3">
            Guest experience
          </h3>
          <p className="text-[13px] text-[#5F6368] mb-4 leading-relaxed">
            This is what your guests see when they access concierge services
            through the mobile app.
          </p>
          <div className="flex justify-center">
            <div
              style={{
                width: 160,
                height: 326,
                borderRadius: 28,
                background: "#000",
                border: "1px solid rgba(0,0,0,0.1)",
                position: "relative",
                boxShadow:
                  "0 15px 40px rgba(0,0,0,0.1), 0 5px 15px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 6,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 48,
                  height: 14,
                  borderRadius: 7,
                  background: "#000",
                  zIndex: 20,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: 3,
                  left: 3,
                  right: 3,
                  bottom: 3,
                  borderRadius: 25,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    width: PhoneDemo.WIDTH,
                    height: PhoneDemo.HEIGHT,
                    transform: `scale(${152 / PhoneDemo.WIDTH})`,
                    transformOrigin: "top left",
                  }}
                >
                  <PhoneDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {status.cancelAtPeriodEnd && (
        <div
          className="bg-amber-50 border border-amber-200 p-4"
          style={{ borderRadius: 12 }}
        >
          <p className="text-[13px] text-amber-800">
            Your subscription will remain active until{" "}
            <strong>
              {new Date(status.currentPeriodEnd).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </strong>
            . After that, concierge services will be disabled for your guests.
          </p>
        </div>
      )}
    </div>
  );
}
